import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { useCommandPaletteContext } from '../CommandPaletteContext';
import { useRegisterCommands, useRegisterPage, useRegisterShortcut, usePluginNavigation, useDebouncedSearch } from '../hooks';
import { CommandList } from '../components/CommandList';
import { CommandsSkeleton } from '../components/CommandsSkeleton';
import { setProject, setRelease, setWorkspace, useScope } from '@/redux/scope';
import { workspaceService, projectService, releaseService } from '@/app/providers/services';
import { getReleaseUiName } from '@/features/release';
import { useFullRelease } from '@/common/hooks_useFullRelease';
import { getReleaseEnvLabel } from '@/features/release';
import Avatar from "@imminently/decisively-ui/Avatar";
import type { Command as CommandType } from '../types';
import { isArray } from 'lodash';
import { isGuid } from '@/lib/utils';
import { Workspace, Project, Release } from '@packages/commons';
import { Footer } from '../components/CommandFooter';

const PLUGIN_ID = 'navigation-plugin';
const WORKSPACE_PAGE = 'workspace';
const PROJECT_PAGE = 'project';
const RELEASE_PAGE = 'release';
const WORKSPACE_PLACEHOLDER = 'Search workspaces...';
const PROJECT_PLACEHOLDER = 'Search projects...';
const RELEASE_PLACEHOLDER = 'Search releases...';


// ---------------------------------------------------------------------------
// WorkspacePage — rendered when the palette navigates to WORKSPACE_PAGE
// ---------------------------------------------------------------------------

const WorkspacePage: React.FC = () => {
  const debouncedSearch = useDebouncedSearch();
  const { navigateTo } = usePluginNavigation(PLUGIN_ID);
  const dispatch = useDispatch();
  const { setLoading } = useCommandPaletteContext();

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  // isLoading: true only on the initial fetch (no data yet)
  const [isLoading, setIsLoading] = useState(true);

  // Load workspaces whenever search changes
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(WORKSPACE_PAGE, true);
      try {
        const filter: any = { sort: { field: 'created', order: 'desc' } as const };

        if (debouncedSearch) {
          if (isGuid(debouncedSearch)) {
            const data = await workspaceService.getOne(debouncedSearch) as Workspace;
            if (!cancelled) {
              setWorkspaces(data?.id && data?.name ? [data] : []);
            }
            return;
          }
          filter.name = `*${debouncedSearch}*`;
        }

        const { data = [] } = await workspaceService.getList(filter);
        if (!cancelled) setWorkspaces((data as Workspace[]).filter((w) => w.id && w.name));
      } catch {
        if (!cancelled) setWorkspaces([]);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setLoading(WORKSPACE_PAGE, false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
      setLoading(WORKSPACE_PAGE, false);
    };
  }, [debouncedSearch, setLoading]);

  const handleSelect = useCallback((workspaceId: string) => {
    dispatch(setWorkspace(workspaceId));
    navigateTo(PROJECT_PAGE, PROJECT_PLACEHOLDER);
  }, [dispatch, navigateTo]);

  const commands = useMemo((): CommandType[] =>
    workspaces.map(w => ({
      id: `workspace-${w.id}`,
      label: w.name,
      description: w.visibility,
      keywords: [w.name, w.id, w.visibility],
      category: 'Workspaces',
      icon: <Avatar size="sm" name={w.name.slice(0, 1).toUpperCase()} />,
      primary: {
        id: `workspace-${w.id}-select`,
        label: 'Select Workspace',
        action: () => handleSelect(w.id),
      },
    })),
  [workspaces, handleSelect]);

  if (isLoading) return <CommandsSkeleton />;

  return (
    <>
      <CommandList commands={commands} emptyMessage="No workspaces found." />
      <Footer/>
    </>
  );
};

// ---------------------------------------------------------------------------
// ProjectPage — rendered when the palette navigates to PROJECT_PAGE
// ---------------------------------------------------------------------------

const ProjectPage: React.FC = () => {
  const { close, setLoading } = useCommandPaletteContext();
  const dispatch = useDispatch();
  const { workspace } = useScope();

  const debouncedSearch = useDebouncedSearch();

  const [projects, setProjects] = useState<Project[]>([]);

  // Load projects whenever workspace or search changes
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(PROJECT_PAGE, true);
      try {
        const filter: any = {
          workspace,
          sort: { field: 'created', order: 'desc' } as const,
        };

        if (debouncedSearch) {
          if (isGuid(debouncedSearch)) {
            const data = await projectService.getOne(debouncedSearch) as Project;
            if (!cancelled) setProjects(data?.id && data?.name ? [data] : []);
            return;
          }
          filter.name = `*${debouncedSearch}*`;
        }

        const { data } = await projectService.getList(filter);
        if (!cancelled) {
          if (!isArray(data) && data !== undefined && data !== null) {
            console.warn('Expected projectService.getList to return an array, but got:', data);
            setProjects([]);
            return;
          }
          setProjects((data as Project[]).filter((p) => p.id && p.name));
        }
      } catch {
        if (!cancelled) setProjects([]);
      } finally {
        if (!cancelled) setLoading(PROJECT_PAGE, false);
      }
    };

    load();
    return () => {
      cancelled = true;
      setLoading(PROJECT_PAGE, false);
    };
  }, [workspace, debouncedSearch, setLoading]);

  const handleSelect = useCallback((projectId: string) => {
    dispatch(setProject(projectId, { resetRelease: true }));
    dispatch(push('/build'));
    close();
  }, [dispatch, close]);

  const commands = useMemo((): CommandType[] =>
    projects.map(p => ({
      id: `project-${p.id}`,
      label: p.name,
      description: p.description || 'Project',
      keywords: [p.name, p.id, p.description, p.visibility, ...p.tags],
      category: 'Projects',
      icon: <Avatar size="sm" name={p.name.slice(0, 1).toUpperCase()} />,
      primary: {
        id: `project-${p.id}-select`,
        label: 'Open Project',
        action: () => handleSelect(p.id),
      },
    })),
  [projects, handleSelect]);

  return <CommandList commands={commands} emptyMessage="No projects found." />;
};

// ---------------------------------------------------------------------------
// ReleasePage — rendered when the palette navigates to RELEASE_PAGE
// ---------------------------------------------------------------------------

const ReleasePage: React.FC = () => {
  const { close, setLoading } = useCommandPaletteContext();
  const dispatch = useDispatch();
  const { project } = useScope();
  const debouncedSearch = useDebouncedSearch();

  const [releases, setReleases] = useState<Release[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!project) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(RELEASE_PAGE, true);
      try {
        const filter: any = {
          model: project,
          sort: { field: 'releaseNo', order: 'desc' } as const,
        };
        if (debouncedSearch) {
          filter.description = `*${debouncedSearch}*`;
        }
        const { data = [] } = await releaseService.getList(filter);
        if (!cancelled) setReleases((data as Release[]).filter((r) => r.id));
      } catch {
        if (!cancelled) setReleases([]);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setLoading(RELEASE_PAGE, false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
      setLoading(RELEASE_PAGE, false);
    };
  }, [project, debouncedSearch, setLoading]);

  const handleSelect = useCallback((releaseId: string) => {
    dispatch(setRelease(releaseId));
    dispatch(push('/build'));
    close();
  }, [dispatch, close]);

  const commands = useMemo((): CommandType[] =>
    releases.map(r => ({
      id: `release-${r.id}`,
      label: getReleaseUiName(r),
      description: [getReleaseEnvLabel(r.env), r.description].filter(Boolean).join(' | '),
      keywords: [r.id, String(r.releaseNo), r.env, getReleaseEnvLabel(r.env), r.description].filter(Boolean) as string[],
      category: 'Releases',
      primary: {
        id: `release-${r.id}-select`,
        label: 'Open Release',
        action: () => handleSelect(r.id),
      },
    })),
  [releases, handleSelect]);

  if (isLoading) return <CommandsSkeleton />;

  return <CommandList delay={300} commands={commands} emptyMessage="No releases found." />;
};

// ---------------------------------------------------------------------------
// NavigationPlugin — registers global commands + two pages
// ---------------------------------------------------------------------------

/**
 * NavigationPlugin - Provides workspace and project navigation
 *
 * Global commands (shown in the main palette):
 *   - "Go to Workspace" → opens the workspace page
 *   - "Go to Project"   → opens the project page
 *
 * Pages:
 *   - workspace: WorkspacePage — searchable workspace list; selecting one
 *                auto-enters the project page
 *   - project:   ProjectPage  — searchable project list; selecting one
 *                navigates to /build
 */
export const NavigationPlugin: React.FC = () => {
  const { navigateTo, openAndNavigate } = usePluginNavigation(PLUGIN_ID);
  const { close } = useCommandPaletteContext();
  const dispatch = useDispatch();
  const currentRelease = useFullRelease();

  // ---------------------------------------------------------------------------
  // Global commands
  // ---------------------------------------------------------------------------
  const globalCommands = useMemo((): CommandType[] => [
    {
      id: 'open-workspace-navigation',
      label: 'Go to Workspace',
      description: 'Navigate to a workspace',
      category: 'Navigation',
      shortcut: 'mod+alt+w',
      keywords: ['workspace', 'navigate', 'switch'],
      primary: {
        id: 'open-workspace-navigation-action',
        label: 'Open Workspace Navigation',
        action: () => navigateTo(WORKSPACE_PAGE, WORKSPACE_PLACEHOLDER),
      },
    },
    {
      id: 'open-project-navigation',
      label: 'Go to Project',
      description: 'Navigate to a project',
      category: 'Navigation',
      shortcut: 'mod+alt+p',
      keywords: ['project', 'navigate', 'switch'],
      primary: {
        id: 'open-project-navigation-action',
        label: 'Open Project Navigation',
        action: () => navigateTo(PROJECT_PAGE, PROJECT_PLACEHOLDER),
      },
    },
    {
      id: 'search-releases',
      label: 'Search Releases',
      description: 'Search and switch to a release',
      category: 'Navigation',
      shortcut: 'mod+alt+r',
      keywords: ['release', 'search', 'switch', 'version'],
      primary: {
        id: 'search-releases-action',
        label: 'Search Releases',
        action: () => navigateTo(RELEASE_PAGE, RELEASE_PLACEHOLDER),
      },
    },
    {
      id: 'go-to-current-release',
      label: 'Go to Current Release',
      description: 'Navigate to the current release build',
      category: 'Navigation',
      keywords: ['release', 'build', 'current', 'navigate'],
      primary: {
        id: 'go-to-current-release-action',
        label: 'Go to Current Release',
        action: () => {
          dispatch(push('/build'));
          close();
        },
      },
    }
  ], [navigateTo, dispatch, close, currentRelease]);

  useRegisterCommands({ pluginId: PLUGIN_ID, commands: globalCommands });

  // ---------------------------------------------------------------------------
  // Page registrations
  // ---------------------------------------------------------------------------
  useRegisterPage({ pluginId: PLUGIN_ID, pageId: WORKSPACE_PAGE, component: WorkspacePage });
  useRegisterPage({ pluginId: PLUGIN_ID, pageId: PROJECT_PAGE, component: ProjectPage });
  useRegisterPage({ pluginId: PLUGIN_ID, pageId: RELEASE_PAGE, component: ReleasePage });

  // ---------------------------------------------------------------------------
  // Keyboard shortcuts that jump directly to a page
  // ---------------------------------------------------------------------------
  useRegisterShortcut({
    shortcut: 'mod+alt+w',
    label: 'Go to Workspace',
    handler: () => openAndNavigate(WORKSPACE_PAGE, WORKSPACE_PLACEHOLDER),
  });

  useRegisterShortcut({
    shortcut: 'mod+alt+p',
    label: 'Go to Project',
    handler: () => openAndNavigate(PROJECT_PAGE, PROJECT_PLACEHOLDER),
  });

  useRegisterShortcut({
    shortcut: 'mod+alt+r',
    label: 'Search Releases',
    handler: () => openAndNavigate(RELEASE_PAGE, RELEASE_PLACEHOLDER),
  });

  return null;
};
